'use client';

import { useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { supabase } from '@/lib/supabase';

interface PublicTestimonial {
  id: string;
  testimonial_text: string;
  rating: number | null;
  display_name: string | null;
  student_context: string | null;
}

export function ApprovedTestimonials() {
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const posthog = usePostHog();
  const captured = useRef(false);

  useEffect(() => {
    if (!supabase) return;
    void supabase
      .from('testimonials')
      .select('id,testimonial_text,rating,display_name,student_context')
      .eq('approved', true)
      .eq('public_permission', true)
      .order('created_at', { ascending: false })
      .limit(3)
      .then(({ data, error }) => {
        if (!error && data) setTestimonials(data as PublicTestimonial[]);
      });
  }, []);

  useEffect(() => {
    if (!testimonials.length || captured.current) return;
    captured.current = true;
    posthog?.capture('testimonial_proof_viewed', { testimonial_count: testimonials.length, placement: 'homepage' });
  }, [posthog, testimonials.length]);

  if (!testimonials.length) return null;

  return (
    <section className="border-y border-border bg-surface px-5 py-16 sm:px-8 sm:py-20">
      <div className="mx-auto max-w-5xl">
        <div className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">From students who chose to share</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground">What the workflow gives them back</h2>
        </div>
        <div className="mt-9 grid gap-5 md:grid-cols-3">
          {testimonials.map(testimonial => (
            <figure key={testimonial.id} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              {testimonial.rating && <div className="text-sm tracking-widest text-amber-500" aria-label={`${testimonial.rating} out of 5 stars`}>{'★'.repeat(testimonial.rating)}</div>}
              <blockquote className="mt-3 text-sm leading-7 text-foreground">“{testimonial.testimonial_text}”</blockquote>
              {(testimonial.display_name || testimonial.student_context) && (
                <figcaption className="mt-5 text-xs font-semibold text-muted">
                  {[testimonial.display_name, testimonial.student_context].filter(Boolean).join(' · ')}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
